/**
 * Author sales dashboard — Legal Pro / Business.
 * Shows balance, recent sales, payout history, and a payout request modal.
 */
import React, { useCallback, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { toast } from "sonner";
import { Wallet, Banknote, TrendingUp, Loader2, Receipt, CheckCircle2, Eye, Trash2, Send } from "lucide-react";
import { Link } from "react-router-dom";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function MarketplaceSales() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState({ balance: null, recent_sales: [], payouts: [] });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [iban, setIban] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const allowed = user && (user.subscription_tier === "legal_pro" || user.subscription_tier === "business");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, tr] = await Promise.all([
        api.get("/marketplace/sales/me"),
        api.get("/marketplace/templates/me"),
      ]);
      setData(r.data);
      setTemplates(tr.data.items || []);
    } catch (e) {
      // gated, fall through
    } finally {
      setLoading(false);
    }
  }, []);

  useImperativeLoad(() => { if (allowed) load(); }, [allowed]);

  const publishDraft = useCallback(async (tpl) => {
    try {
      await api.post(`/marketplace/templates/${tpl.template_id}/publish`);
      toast.success(t("marketplace.toast_template_published"));
      load();
    } catch (e) {
      toast.error(t("marketplace.err_publish"));
    }
  }, [load, t]);

  const removeTemplate = useCallback(async (tpl) => {
    if (!window.confirm(t("marketplace.publish_remove") + "?")) return;
    try {
      await api.delete(`/marketplace/templates/${tpl.template_id}`);
      toast.success(t("marketplace.toast_template_removed"));
      load();
    } catch (e) {
      toast.error("Could not remove");
    }
  }, [load, t]);

  const handlePayoutRequest = useCallback(async () => {
    setSubmitting(true);
    try {
      await api.post("/marketplace/payout-request", {
        iban: iban.trim() || null,
        notes: notes.trim() || null,
      });
      toast.success(t("marketplace.sales_payout_success"));
      setDialogOpen(false);
      setIban(""); setNotes("");
      load();
    } catch (e) {
      const code = e?.response?.data?.detail?.error_code;
      if (code === "PAYOUT_ALREADY_PENDING") {
        toast.error(t("marketplace.sales_payout_pending"));
      } else if (code === "PAYOUT_BELOW_THRESHOLD") {
        toast.error(t("marketplace.sales_payout_below").replace("{threshold}", 50));
      } else {
        toast.error("Could not request payout");
      }
    } finally {
      setSubmitting(false);
    }
  }, [iban, notes, load, t]);

  if (!allowed) {
    return (
      <div className="flex min-h-full items-center justify-center bg-[#0A0C1F] p-10 text-white">
        <p>Legal Pro tier required.</p>
      </div>
    );
  }

  if (loading || !data.balance) {
    return (
      <div className="flex min-h-full items-center justify-center bg-[#0A0C1F] p-10 text-white">
        <Loader2 className="h-6 w-6 animate-spin text-[#9ACD32]" />
      </div>
    );
  }

  const b = data.balance;
  const canRequest = b.available_balance_eur >= b.payout_threshold_eur;
  const hasPendingPayout = data.payouts.some((p) => p.status === "requested");

  return (
    <div className="h-full overflow-y-auto bg-[#0A0C1F] text-white" data-testid="marketplace-sales-page">
      <section className="mx-auto max-w-5xl px-6 py-10 lg:px-12">
        <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
          {t("marketplace.sales_title")}
        </h1>

        {/* KPIs */}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="sales-kpis">
          <Card className="border-[#9ACD32]/30 bg-[#9ACD32]/10 p-5">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-[#9ACD32]">
              <Wallet className="h-3.5 w-3.5" /> {t("marketplace.sales_balance_label")}
            </div>
            <p className="mt-2 font-mono text-3xl font-extrabold text-white" data-testid="sales-balance">
              €{b.available_balance_eur.toFixed(2)}
            </p>
            <p className="mt-1 text-[11px] text-slate-400">
              {t("marketplace.sales_payout_threshold").replace("{threshold}", b.payout_threshold_eur)}
            </p>
            {(b.referral_credit_balance_eur > 0 || b.marketplace_balance_eur > 0) && (
              <p className="mt-2 border-t border-[#9ACD32]/20 pt-2 font-mono text-[10px] uppercase tracking-wider text-slate-400" data-testid="sales-balance-breakdown">
                €{(b.marketplace_balance_eur || 0).toFixed(2)} mkt + €{(b.referral_credit_balance_eur || 0).toFixed(2)} aff
              </p>
            )}
          </Card>
          <Card className="border-slate-800 bg-slate-900/60 p-5" data-testid="sales-lifetime">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <TrendingUp className="h-3.5 w-3.5" /> {t("marketplace.sales_lifetime_label")}
            </div>
            <p className="mt-2 font-mono text-3xl font-extrabold text-white">€{b.lifetime_payout_eur.toFixed(2)}</p>
            <p className="mt-1 text-[11px] text-slate-500">({b.lifetime_gross_eur.toFixed(2)} − 15%)</p>
          </Card>
          <Card className="border-slate-800 bg-slate-900/60 p-5" data-testid="sales-count">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <Receipt className="h-3.5 w-3.5" /> {t("marketplace.sales_sales_label")}
            </div>
            <p className="mt-2 font-mono text-3xl font-extrabold text-white">{b.lifetime_sales}</p>
          </Card>
          <Card className="border-slate-800 bg-slate-900/60 p-5" data-testid="sales-paid-out">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <Banknote className="h-3.5 w-3.5" /> {t("marketplace.sales_paid_out_label")}
            </div>
            <p className="mt-2 font-mono text-3xl font-extrabold text-white">€{b.paid_out_eur.toFixed(2)}</p>
          </Card>
        </div>

        {/* Payout request */}
        <div className="mt-6 flex flex-col items-stretch gap-3 rounded-2xl border border-slate-800 bg-slate-900/80 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-display text-base font-bold text-white">
              {canRequest && !hasPendingPayout
                ? `€${b.available_balance_eur.toFixed(2)} ${t("marketplace.sales_payout_cta").toLowerCase()}`
                : hasPendingPayout
                  ? t("marketplace.sales_payout_pending")
                  : t("marketplace.sales_payout_below").replace("{threshold}", b.payout_threshold_eur)}
            </p>
          </div>
          {canRequest && !hasPendingPayout && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
                  data-testid="sales-payout-cta"
                >
                  <Banknote className="mr-2 h-4 w-4" /> {t("marketplace.sales_payout_cta")}
                </Button>
              </DialogTrigger>
              <DialogContent className="border-slate-700 bg-[#0A0C1F] text-white">
                <DialogHeader>
                  <DialogTitle>{t("marketplace.sales_payout_modal_title")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase text-slate-400">
                      {t("marketplace.sales_payout_iban_label")}
                    </label>
                    <Input
                      value={iban}
                      onChange={(e) => setIban(e.target.value)}
                      placeholder={t("marketplace.sales_payout_iban_ph")}
                      className="border-slate-700 bg-slate-900 font-mono text-white"
                      data-testid="sales-payout-iban"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase text-slate-400">
                      {t("marketplace.sales_payout_notes_label")}
                    </label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="border-slate-700 bg-slate-900 text-white"
                      data-testid="sales-payout-notes"
                    />
                  </div>
                  <Button
                    onClick={handlePayoutRequest}
                    disabled={submitting}
                    className="w-full rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
                    data-testid="sales-payout-submit"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("marketplace.sales_payout_submit")}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* My templates */}
        <section className="mt-10" data-testid="sales-my-templates">
          <h2 className="mb-4 font-display text-xl font-bold text-white">{t("marketplace.publish_drafts_title")}</h2>
          {templates.length === 0 ? (
            <p className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 text-center text-sm text-slate-400">
              <Link to="/legal/atti" className="text-[#9ACD32] underline">
                {t("marketplace.publish_open_archive_cta")}
              </Link>
            </p>
          ) : (
            <div className="space-y-2">
              {templates.map((tpl) => (
                <Card key={tpl.template_id} className="flex flex-wrap items-center justify-between gap-3 border-slate-800 bg-slate-900/60 p-4" data-testid={`sales-template-${tpl.template_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider ${
                        tpl.status === "published" ? "bg-[#9ACD32]/15 text-[#9ACD32]" : tpl.status === "draft" ? "bg-[#D4A937]/15 text-[#F4C955]" : "bg-rose-500/15 text-rose-300"
                      }`}>
                        {tpl.status === "published" ? t("marketplace.publish_status_published") : tpl.status === "draft" ? t("marketplace.publish_status_draft") : t("marketplace.publish_status_removed")}
                      </span>
                      <span className="rounded-full bg-slate-800 px-2 py-0.5 font-mono text-[10px] text-slate-300">€{tpl.price_eur}</span>
                    </div>
                    <p className="mt-1 truncate font-display text-sm font-semibold text-white">{tpl.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                      {tpl.purchases_count} sold · €{(tpl.revenue_total_eur || 0).toFixed(2)} gross
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {tpl.status === "published" && (
                      <Link to={`/marketplace/template/${tpl.template_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-200 hover:bg-slate-800" data-testid={`sales-template-view-${tpl.template_id}`}>
                        <Eye className="h-3 w-3" /> View
                      </Link>
                    )}
                    {tpl.status === "draft" && (
                      <Button size="sm" onClick={() => publishDraft(tpl)} className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]" data-testid={`sales-template-publish-${tpl.template_id}`}>
                        <Send className="mr-1 h-3 w-3" /> Publish
                      </Button>
                    )}
                    {tpl.status !== "removed" && (
                      <Button size="sm" variant="ghost" onClick={() => removeTemplate(tpl)} className="rounded-full text-rose-300 hover:bg-rose-500/10" data-testid={`sales-template-remove-${tpl.template_id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Recent sales */}
        <section className="mt-10" data-testid="sales-recent">
          <h2 className="mb-4 font-display text-xl font-bold text-white">{t("marketplace.sales_recent_title")}</h2>
          {data.recent_sales.length === 0 ? (
            <p className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 text-center text-sm text-slate-400">
              {t("marketplace.sales_no_sales")}
            </p>
          ) : (
            <div className="space-y-2">
              {data.recent_sales.map((sale) => (
                <Card key={sale.purchase_id} className="flex items-center justify-between border-slate-800 bg-slate-900/60 p-4">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#F4C955]">{sale.label}</p>
                    <p className="truncate font-display text-sm font-semibold text-white">{sale.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{formatDate(sale.paid_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-base font-extrabold text-[#9ACD32]">+€{sale.author_payout_eur.toFixed(2)}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">of €{sale.price_eur}</p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Payout history */}
        {data.payouts.length > 0 && (
          <section className="mt-10" data-testid="sales-payout-history">
            <h2 className="mb-4 font-display text-xl font-bold text-white">{t("marketplace.sales_payouts_title")}</h2>
            <div className="space-y-2">
              {data.payouts.map((p) => (
                <Card key={p.payout_id} className="flex items-center justify-between border-slate-800 bg-slate-900/60 p-4">
                  <div>
                    <p className="font-mono text-sm font-semibold text-white">€{p.amount_eur.toFixed(2)}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">
                      {p.status === "paid" ? `Paid · ${formatDate(p.paid_at)}` : `${p.status} · ${formatDate(p.requested_at)}`}
                    </p>
                  </div>
                  {p.status === "paid" && <CheckCircle2 className="h-5 w-5 text-[#9ACD32]" />}
                </Card>
              ))}
            </div>
          </section>
        )}
      </section>
    </div>
  );
}
